import { featureFlags } from "$global-providers/feature-flags-provider/feature-flags";
import { useFeatureFlag } from "$global-providers/feature-flags-provider/provider";
import {
  RXDB_NOTIFICATION_COLLECTION_NAME,
  type Notification as RxNotification,
} from "$global-providers/rxdb-provider/collections/notification";
import {
  RXDB_NOTIFICATION_BELL_STATE_COLLECTION_NAME,
  type NotificationBellState as RxNotificationBellState,
} from "$global-providers/rxdb-provider/collections/notification-bell-state";
import {
  RXDB_NOTIFICATION_DISMISSAL_COLLECTION_NAME,
  type NotificationDismissal as RxNotificationDismissal,
} from "$global-providers/rxdb-provider/collections/notification-dismissal";
import {
  RXDB_NOTIFICATION_PREFERENCE_COLLECTION_NAME,
  type NotificationPreference as RxNotificationPreference,
} from "$global-providers/rxdb-provider/collections/notification-preference";
import { RxDBContext } from "$global-providers/rxdb-provider/provider";
import { useServerTime } from "$global-providers/server-time-provider/provider";
import { useLazyUser } from "$global-providers/user-provider";
import {
  createContext,
  type ReactNode,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import type { RxCollection } from "rxdb";
import { bellStateId, dismissalId, notificationId } from "./deterministic-ids";
import type {
  BellNotification,
  BellState,
  CategoryPreference,
  NotificationCategoryDefinition,
  NotificationsEmitter,
} from "./types";

type CategoryRegistry = Record<string, NotificationCategoryDefinition>;

type NotificationsContextValue = {
  emitter: NotificationsEmitter;
  bell: BellState;
  /**
   * The currently-mounted set of category definitions. Adapters register via
   * `useRegisterNotificationCategory` on mount; the registry is in-memory and
   * re-derives on each session.
   */
  registry: CategoryRegistry;
  registerCategory: (definition: NotificationCategoryDefinition) => void;
  unregisterCategory: (categoryKey: string) => void;
  preferences: CategoryPreference[];
  setPreferenceEnabled: (
    categoryKey: string,
    enabled: boolean,
  ) => Promise<void>;
};

const NotificationsContext = createContext<NotificationsContextValue | null>(
  null,
);

const useRxCollectionsFromContext = () => {
  const ctx = useContext(RxDBContext);
  const db = ctx?.db;
  return useMemo(
    () => ({
      notification:
        (db?.[RXDB_NOTIFICATION_COLLECTION_NAME] as
          | RxCollection<RxNotification>
          | undefined) ?? null,
      bellState:
        (db?.[RXDB_NOTIFICATION_BELL_STATE_COLLECTION_NAME] as
          | RxCollection<RxNotificationBellState>
          | undefined) ?? null,
      dismissal:
        (db?.[RXDB_NOTIFICATION_DISMISSAL_COLLECTION_NAME] as
          | RxCollection<RxNotificationDismissal>
          | undefined) ?? null,
      preference:
        (db?.[RXDB_NOTIFICATION_PREFERENCE_COLLECTION_NAME] as
          | RxCollection<RxNotificationPreference>
          | undefined) ?? null,
    }),
    [db],
  );
};

const useRxRows = <T,>(collection: RxCollection<T> | null): T[] => {
  const [rows, setRows] = useState<T[]>([]);
  useEffect(() => {
    if (!collection) {
      setRows([]);
      return;
    }
    const sub = collection
      .find()
      .$.subscribe((docs) => setRows(docs.map((d) => d.toJSON() as T)));
    return () => sub.unsubscribe();
  }, [collection]);
  return rows;
};

const syncableMetaInit = (userId: string, nowIso: string) => ({
  createdAt: nowIso,
  createdByUserId: userId,
  updatedAt: nowIso,
  updatedByUserId: userId,
});

export const NotificationsProvider = ({
  children,
}: {
  children: ReactNode;
}) => {
  const user = useLazyUser();
  const userId = user?.id ?? null;
  const [enabled] = useFeatureFlag<boolean>(featureFlags.notifications.center);
  const { getCurrentTime } = useServerTime();
  const collections = useRxCollectionsFromContext();
  const allNotifications = useRxRows<RxNotification>(collections.notification);
  const allBellStates = useRxRows<RxNotificationBellState>(
    collections.bellState,
  );
  const allDismissals = useRxRows<RxNotificationDismissal>(
    collections.dismissal,
  );
  const allPreferences = useRxRows<RxNotificationPreference>(
    collections.preference,
  );

  // Shared facility devices keep RxDB across logout (intentional cache-sharing
  // — see project_rxdb_facility_cache_design memory / ADR-004 §1). Every
  // per-user piece of state is therefore filtered by `userId` at read time so
  // one user's bell never reflects another's emit / dismissal / preference.
  const notifications = useMemo(
    () => (userId ? allNotifications.filter((n) => n.userId === userId) : []),
    [allNotifications, userId],
  );
  const bellStates = useMemo(
    () => (userId ? allBellStates.filter((b) => b.userId === userId) : []),
    [allBellStates, userId],
  );
  const dismissals = useMemo(
    () => (userId ? allDismissals.filter((d) => d.userId === userId) : []),
    [allDismissals, userId],
  );
  const preferenceRows = useMemo(
    () => (userId ? allPreferences.filter((p) => p.userId === userId) : []),
    [allPreferences, userId],
  );

  const [registry, setRegistry] = useState<CategoryRegistry>({});

  const registerCategory = useCallback(
    (definition: NotificationCategoryDefinition) => {
      setRegistry((prev) =>
        prev[definition.categoryKey] === definition
          ? prev
          : { ...prev, [definition.categoryKey]: definition },
      );
    },
    [],
  );
  const unregisterCategory = useCallback((categoryKey: string) => {
    setRegistry((prev) => {
      if (!(categoryKey in prev)) return prev;
      const next = { ...prev };
      delete next[categoryKey];
      return next;
    });
  }, []);

  const myBellState = useMemo(
    () => bellStates.find((b) => b.userId === userId) ?? null,
    [bellStates, userId],
  );

  const dismissedKeyVersions = useMemo(() => {
    const map = new Map<string, Set<string | null>>();
    for (const d of dismissals) {
      const set = map.get(d.key) ?? new Set();
      set.add(d.versionAtDismissal ?? null);
      map.set(d.key, set);
    }
    return map;
  }, [dismissals]);

  const preferenceByKey = useMemo(() => {
    const map = new Map<string, boolean>();
    for (const p of preferenceRows) map.set(p.categoryKey, p.enabled);
    return map;
  }, [preferenceRows]);

  const isCategoryEnabled = useCallback(
    (categoryKey: string, severity: BellNotification["severity"]) => {
      if (severity === "ACTION_REQUIRED") return true;
      const def = registry[categoryKey];
      if (def?.alwaysOn) return true;
      const stored = preferenceByKey.get(categoryKey);
      return stored ?? true;
    },
    [preferenceByKey, registry],
  );

  const lastBellOpenedAt = myBellState?.lastBellOpenedAt ?? null;

  // `expiryTick` re-runs `visibleNotifications` when the earliest expiring
  // row's `expiresAt` is reached. We only arm a timer when at least one row
  // has an `expiresAt` in the future, so idle sessions don't pay re-render
  // cost (Copilot review on PR #2115).
  const [expiryTick, setExpiryTick] = useState(0);
  useEffect(() => {
    // Self-re-arming chain: when notifications change we (re)compute the
    // earliest future `expiresAt` and schedule one timer to it. When that
    // fires we bump `expiryTick` (which forces `visibleNotifications` to
    // re-derive — see its deps below) and arm the next chunk.
    let timerId: ReturnType<typeof setTimeout> | undefined;
    const arm = () => {
      const upcoming = notifications
        .map((n) => (n.expiresAt ? new Date(n.expiresAt).getTime() : null))
        .filter((t): t is number => t !== null && t > Date.now())
        .sort((a, b) => a - b);
      if (upcoming.length === 0) return;
      // setTimeout silently clamps delays above the 32-bit limit (~24.8 days)
      // to 1ms, which would fire the tick immediately and tight-loop until
      // the deadline is within range. Cap and re-arm on the next chunk.
      const delay = Math.min(upcoming[0] - Date.now(), 2_147_483_647);
      timerId = setTimeout(() => {
        setExpiryTick((t) => t + 1);
        arm();
      }, delay);
    };
    arm();
    return () => {
      if (timerId !== undefined) clearTimeout(timerId);
    };
  }, [notifications]);

  const visibleNotifications = useMemo<BellNotification[]>(() => {
    // `expiryTick` is read so React re-derives this list when a deadline hits.
    void expiryTick;
    // Local `Date.now()` is intentional here: this is render-time UX filtering
    // and a few seconds of clock drift either way is fine. The server-time
    // hook throws "Server time not yet known" until the first API exchange
    // settles, and we can't crash the bell on initial mount. All *writes* —
    // createdAt, dismissedAt, lastBellOpenedAt — still go through
    // `getCurrentTime()` in the event handlers below.
    const now = Date.now();
    return notifications
      .filter((n) => {
        if (n.expiresAt && new Date(n.expiresAt).getTime() <= now) return false;
        const dismissed = dismissedKeyVersions.get(n.key);
        if (dismissed?.has(n.version ?? null)) return false;
        if (!isCategoryEnabled(n.category, n.severity)) return false;
        return true;
      })
      .map((n) => ({
        id: n.id,
        key: n.key,
        title: n.title,
        body: n.body,
        severity: n.severity,
        category: n.category,
        icon: n.icon,
        actionUrl: n.actionUrl,
        version: n.version,
        createdAt: n.createdAt,
        unread:
          !lastBellOpenedAt ||
          new Date(n.createdAt).getTime() >
            new Date(lastBellOpenedAt).getTime(),
      }))
      .sort(
        (a, b) =>
          new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime(),
      );
  }, [
    notifications,
    dismissedKeyVersions,
    isCategoryEnabled,
    lastBellOpenedAt,
    expiryTick,
  ]);

  const actionRequiredCount = visibleNotifications.filter(
    (n) => n.unread && n.severity === "ACTION_REQUIRED",
  ).length;
  const unreadCount = visibleNotifications.filter((n) => n.unread).length;
  const kind: BellState["kind"] =
    actionRequiredCount > 0 ? "badge" : unreadCount > 0 ? "dot" : "quiet";

  const upsertBellWatermark = useCallback(
    async (patch: {
      lastBellOpenedAt?: string;
      lastMarkAllReadAt?: string;
    }) => {
      const col = collections.bellState;
      if (!col || !userId) return;
      const nowIso = getCurrentTime().toISOString();
      const id = bellStateId(userId);
      // Atomic CAS via `incrementalModify`: the mutator runs with the latest
      // stored document at write time, so overlapping `openBell` and
      // `markAllRead` calls can't clobber each other's fields. If the row
      // doesn't exist yet, insert it; otherwise modify in place.
      const existing = await col.findOne(id).exec();
      if (!existing) {
        await col.insert({
          id,
          userId,
          lastBellOpenedAt: patch.lastBellOpenedAt ?? null,
          lastMarkAllReadAt: patch.lastMarkAllReadAt ?? null,
          meta: syncableMetaInit(userId, nowIso),
        });
        return;
      }
      await existing.incrementalModify((doc) => ({
        ...doc,
        lastBellOpenedAt: patch.lastBellOpenedAt ?? doc.lastBellOpenedAt,
        lastMarkAllReadAt: patch.lastMarkAllReadAt ?? doc.lastMarkAllReadAt,
        meta: {
          ...doc.meta,
          updatedAt: nowIso,
          updatedByUserId: userId,
        },
      }));
    },
    [collections.bellState, userId, getCurrentTime],
  );

  const openBell = useCallback(
    () =>
      upsertBellWatermark({
        lastBellOpenedAt: getCurrentTime().toISOString(),
      }),
    [upsertBellWatermark, getCurrentTime],
  );

  const markAllRead = useCallback(async () => {
    const now = getCurrentTime().toISOString();
    await upsertBellWatermark({
      lastBellOpenedAt: now,
      lastMarkAllReadAt: now,
    });
  }, [upsertBellWatermark, getCurrentTime]);

  const dismissRow = useCallback(
    async (key: string, version: string | null) => {
      const col = collections.dismissal;
      if (!col || !userId) return;
      const id = dismissalId(userId, key, version);
      const nowIso = getCurrentTime().toISOString();
      // Two-rapid-clicks racing both past a `findOne(id)` check and then both
      // calling `insert(id)` would leave one promise rejected. `insert` itself
      // is rejected on duplicate primary key by RxDB; we catch that as success
      // — the row is already there, no work to do, and the original
      // `dismissedAt` / create-side metadata is preserved.
      try {
        await col.insert({
          id,
          userId,
          key,
          versionAtDismissal: version,
          dismissedAt: nowIso,
          meta: syncableMetaInit(userId, nowIso),
        });
      } catch (err: unknown) {
        const code = (err as { code?: string })?.code;
        // RxDB throws CONFLICT (`COL19`) when the deterministic id already
        // exists. Treat as idempotent success.
        if (code !== "COL19" && code !== "CONFLICT") throw err;
      }
    },
    [collections.dismissal, userId, getCurrentTime],
  );

  const emit = useCallback<NotificationsEmitter["emit"]>(
    async (args) => {
      const col = collections.notification;
      if (!col || !enabled) return;
      if (!userId) return;
      // Single atomic upsert keyed on `${userId}|${key}`. `createdAt` is bumped
      // on every emit so re-emits resurface as unread per ADR-004 §4.
      await col.upsert({
        id: notificationId(userId, args.key),
        userId,
        key: args.key,
        title: args.title,
        body: args.body ?? null,
        severity: args.severity,
        category: args.category,
        icon: args.icon ?? null,
        actionUrl: args.actionUrl ?? null,
        version: args.version ?? null,
        createdAt: getCurrentTime().toISOString(),
        readAt: null,
        expiresAt: args.expiresAt?.toISOString() ?? null,
      });
    },
    [collections.notification, enabled, userId, getCurrentTime],
  );

  const recall = useCallback<NotificationsEmitter["recall"]>(
    async (key) => {
      const col = collections.notification;
      if (!col || !userId) return;
      const doc = await col.findOne(notificationId(userId, key)).exec();
      await doc?.remove();
    },
    [collections.notification, userId],
  );

  const setPreferenceEnabled = useCallback(
    async (categoryKey: string, value: boolean) => {
      const col = collections.preference;
      if (!col || !userId) return;
      await col.upsert({
        id: `${userId}|${categoryKey}`,
        userId,
        categoryKey,
        enabled: value,
      });
    },
    [collections.preference, userId],
  );

  const preferences = useMemo<CategoryPreference[]>(
    () =>
      Object.values(registry).map((def) => ({
        ...def,
        enabled: def.alwaysOn
          ? true
          : (preferenceByKey.get(def.categoryKey) ?? true),
      })),
    [registry, preferenceByKey],
  );

  const bell: BellState = {
    kind,
    actionRequiredCount,
    unreadCount,
    notifications: visibleNotifications,
    markAllRead,
    dismissRow,
    openBell,
  };

  const value: NotificationsContextValue = {
    emitter: { emit, recall },
    bell,
    registry,
    registerCategory,
    unregisterCategory,
    preferences,
    setPreferenceEnabled,
  };

  return (
    <NotificationsContext.Provider value={value}>
      {children}
    </NotificationsContext.Provider>
  );
};

const useNotificationsContext = (): NotificationsContextValue => {
  const ctx = useContext(NotificationsContext);
  if (!ctx) {
    throw new Error(
      "useNotifications* hooks must be used under <NotificationsProvider>",
    );
  }
  return ctx;
};

/** Adapter-facing — emit / recall. Producers depend on this. */
export const useNotificationsEmitter = (): NotificationsEmitter =>
  useNotificationsContext().emitter;

/** Bell-UI facing — derived bell state, open / dismiss / mark-all-read. */
export const useNotificationBell = (): BellState =>
  useNotificationsContext().bell;

/** Settings-UI facing — per-category preferences. */
export const useNotificationPreferences = () => {
  const { preferences, setPreferenceEnabled } = useNotificationsContext();
  return { preferences, setPreferenceEnabled };
};

/**
 * Adapters call this on mount to declare their category to the settings UI.
 * The registration is in-memory; it does not persist. Unmount removes the entry.
 *
 * Note: the registry exists so the settings tab can label categories the
 * producer owns. The notification system itself does not interpret category
 * keys (ADR-004 §1).
 */
export const useRegisterNotificationCategory = (
  definition: NotificationCategoryDefinition,
) => {
  const { registerCategory, unregisterCategory } = useNotificationsContext();
  const ref = useRef(definition);
  ref.current = definition;
  useEffect(() => {
    const def = ref.current;
    registerCategory(def);
    return () => unregisterCategory(def.categoryKey);
  }, [registerCategory, unregisterCategory]);
};
